import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import { fetchUsers } from '../services/userApi'; 
import { fetchLeads } from '../services/api';
import { useNavigate } from 'react-router-dom';

const STATUS_COLORS = {
  New: '#92ef73',
  NA: '#d873ef',
  NI: '#73d6ef53',
  Wrong: '#ef7381',
  Unaviable: '#3483f1',
  Depositor: '#efb573',
  VM: '#73e3ef',
  HU: '#ee050580',
  Callback: '#ffeb3b'
};

// 👉 сюда добавляешь статусы, которые нужно убрать из второго графика
const EXCLUDED_STATUSES_FOR_SECOND_CHART = ['New', 'Unaviable', 'NoLanguage', 'HU', 'VM', 'Wrong', 'Replace', 'Double'];

const Statistics = ({ user }) => {
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedManagerId, setSelectedManagerId] = useState(null); 
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        const token = localStorage.getItem('token');
        const leadsData = await fetchLeads(token);
        const usersData = await fetchUsers(token);
        
        let filteredLeads = leadsData;
        
        if (user?.role === 'user') {
          filteredLeads = leadsData.filter((lead) => lead.managerId === user.id);
        }

        setLeads(filteredLeads);
        if (user?.role === 'admin') {
          setUsers(usersData);
        }
      } catch (err) {
        console.error('Ошибка загрузки данных для статистики:', err);
      }
    };

    loadData();
  }, [user]);

  const filteredLeads = selectedManagerId
    ? leads.filter((lead) => lead.managerId === selectedManagerId)
    : leads;

  // Подсчёт статусов (один раз)
  const statusCounts = filteredLeads.reduce((acc, lead) => {
    acc[lead.status] = (acc[lead.status] || 0) + 1;
    return acc;
  }, {});

  // Данные для 1-го графика — все статусы
  const pieDataAll = Object.entries(statusCounts).map(([status, count]) => ({
    name: status,
    value: count
  }));
  const totalAll = pieDataAll.reduce((s, d) => s + d.value, 0);

  // Данные для 2-го графика — фильтруем часть статусов
  const pieDataFiltered = Object.entries(statusCounts)
    .filter(([status]) => !EXCLUDED_STATUSES_FOR_SECOND_CHART.includes(status))
    .map(([status, count]) => ({
      name: status,
      value: count
    }));
  const totalFiltered = pieDataFiltered.reduce((s, d) => s + d.value, 0);

  const renderLegend = (props) => {
    const { payload } = props;

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: '10px',
          marginTop: '50px',
        }}
      >
        {payload.map((entry, index) => (
          <div
            key={`item-${index}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'white',
              padding: '4px 8px',
              borderRadius: '6px',
              boxShadow: '0 0 2px rgba(0,0,0,0.15)',
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                backgroundColor: entry.color,
                marginRight: 6,
                borderRadius: '50%',
              }}
            />
            <span style={{ color: '#333', fontSize: '14px' }}>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  const RADIAN = Math.PI / 180;

  // фабрика для подписи, чтобы процент считался от нужного total
  const createRenderLabelWithOutline = (total) => ({
    cx,
    cy,
    midAngle,
    outerRadius,
    name,
    payload,
  }) => {
    const radius = outerRadius + 10;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    const pct = total ? Math.round((payload.value / total) * 100) : 0;
    const text = `${name}: ${pct}%`;

    return (
      <g>
        <text
          x={x}
          y={y}
          textAnchor={x > cx ? 'start' : 'end'}
          dominantBaseline="central"
          fill="white"
          stroke="black"
          strokeWidth={3}
          paintOrder="stroke"
          style={{ pointerEvents: 'none' }}
        >
          {text}
        </text>
        <text
          x={x}
          y={y}
          textAnchor={x > cx ? 'start' : 'end'}
          dominantBaseline="central"
          fill={STATUS_COLORS[name] || '#333'}
          style={{ pointerEvents: 'none' }}
        >
          {text}
        </text>
      </g>
    );
  };

  return (
    <div style={{ display: 'flex', padding: '20px' }}>
      {/* Левая панель */}
      <div style={{ width: '200px', marginRight: '40px' }}>
        {/* Кнопка "Назад" — всегда видна */}
        <div style={{ marginBottom: '20px' }}>
          <button
            className="btn btn-secondary w-100"
            onClick={() => navigate('/')}
          >
            ← Назад
          </button>
        </div>

        {/* Менеджеры — только для админа */}
        {user?.role === 'admin' && (
          <>
            <h5>Менеджеры</h5>
            <button
              onClick={() => setSelectedManagerId(null)}
              className="btn w-100 mb-2"
              style={{ backgroundColor: '#97bd3e', color: 'white' }}
            >
              Общий
            </button>

            {users.map((u) => (
              <button
                key={u.id}
                onClick={() => setSelectedManagerId(u.id)}
                className="btn w-100 mb-2"
                style={{ backgroundColor: '#97bd3e', color: 'white' }}
              >
                {u.name}
              </button>
            ))}
          </>
        )}
      </div>

      {/* Правая часть — два графика друг под другом */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '40px' }}>
        <div>
          <h4 className="mb-4">
            {selectedManagerId ? 'Статистика менеджера (все статусы)' : 'Общая статистика (все статусы)'}
          </h4>
          {pieDataAll.length === 0 ? (
            <p>Нет данных для отображения</p>
          ) : (
            <PieChart width={800} height={480}>
              <Pie
                data={pieDataAll}
                dataKey="value"
                nameKey="name"
                cx="60%"
                cy="55%"
                outerRadius={140}
                fill="#8884d8"
                label={createRenderLabelWithOutline(totalAll)}
              >
                {pieDataAll.map((entry, index) => (
                  <Cell
                    key={`cell-all-${index}`}
                    fill={STATUS_COLORS[entry.name] || '#cccccc'}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend
                content={renderLegend}
                layout="horizontal"
                verticalAlign="bottom"
                align="center"
              />
            </PieChart>
          )}
        </div>

        <div>
          <h4 className="mb-4">
            {selectedManagerId
              ? `Статистика менеджера (без: ${EXCLUDED_STATUSES_FOR_SECOND_CHART.join(', ')})`
              : `Общая статистика (без: ${EXCLUDED_STATUSES_FOR_SECOND_CHART.join(', ')})`}
          </h4>
          {pieDataFiltered.length === 0 ? (
            <p>Нет данных для отображения (после фильтрации)</p>
          ) : (
            <PieChart width={800} height={440}>
              <Pie
                data={pieDataFiltered}
                dataKey="value"
                nameKey="name"
                cx="60%"
                cy="50%"
                outerRadius={160}
                fill="#8884d8"
                label={createRenderLabelWithOutline(totalFiltered)}
              >
                {pieDataFiltered.map((entry, index) => (
                  <Cell
                    key={`cell-filtered-${index}`}
                    fill={STATUS_COLORS[entry.name] || '#cccccc'}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend
                content={renderLegend}
                layout="horizontal"
                verticalAlign="bottom"
                align="center"
              />
            </PieChart>
          )}
        </div>
      </div>
    </div>
  );
};

export default Statistics;
